import { useEffect, useState } from 'react';
import { useExternalAccessValue } from '@24i/nxg-sdk-smartott-shared/src/hooks/subscriptions';
import { usePurchaseData } from '@24i/nxg-sdk-smartott-shared/src/context/PurchaseData';
import { logError } from '@24i/nxg-core-utils/src/logger';
import { useTranslation } from 'react-i18next';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';

import useAvailableSubscriptionsQuery from '../../../../../hooks/query/subscriptions/useAvailableSubscriptions';
import { PurchaseSubscriptionScreenProps } from '../../../../../screens/PurchaseSubscriptionScreen/types';
import { useAppNavigation } from '../../../../../context/AppNavigation';
import { setStripeProductDataToStorage } from '../../../utils';
import { useStripe } from '../../../../../context/StripeProvider/index.web';
import { WithErrorModalProps } from '../../../../GenericModal/hoc/ErrorModal';

export const useViewModel = (props: PurchaseSubscriptionScreenProps & WithErrorModalProps) => {
    const { getExternalAccessValue } = useExternalAccessValue();
    const { stripePromise } = useStripe();
    const { previousRoute } = useAppNavigation();
    const purchaseDataClient = usePurchaseData();
    const { data: subscriptionData } = useAvailableSubscriptionsQuery();
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const { t } = useTranslation();
    const router = useRouter();
    const productId = props?.query?.offerId;

    const createCheckoutSession = async () => {
        try {
            const foundSubscription = subscriptionData?.subscriptions.find(
                (v) => v.id === productId || v.externalAccessValue === productId
            );

            const productSubscription = getExternalAccessValue(productId, 'stripe') as {
                priceId?: string;
                productId?: string;
            } | null;
            if (
                !productSubscription ||
                (typeof productSubscription !== 'object' && !foundSubscription?.id)
            )
                return;

            const checkoutSession = await purchaseDataClient.createStripeCheckoutSession({
                productId: foundSubscription?.id as string,
                stripePriceId: productSubscription.priceId as string,
                stripeProductId: productSubscription.productId as string,
                goBackURL: window.location.href,
            });

            setClientSecret(checkoutSession.clientSecret || null);
        } catch (e) {
            logError(e);
            setClientSecret(null);
            props.openErrorModal(
                t('error.B00.title'),
                t('error.B00.body'),
                false,
                undefined,
                undefined,
                () => router.back()
            );
        }
    };

    useEffect(() => {
        if (!productId || !subscriptionData?.subscriptions.length) return;

        setStripeProductDataToStorage(productId, previousRoute).catch();
        createCheckoutSession().catch();
    }, [productId, subscriptionData]);

    return { ...props, clientSecret, stripePromise };
};
